// PST-570 — 주입형 GNB fetcher.
// baseUrl 을 인자로 받아 SSR(앱의 private 엔드포인트)·CSR/IIFE(public 엔드포인트) 양쪽을 지원한다.
// react-query 의존 없음. fetch 기반(Node18 global fetch + 브라우저 모두 동작) → SSR 안전.
// 소비처가 자기 인증/헤더를 주입할 수 있도록 RequestInit 를 받는다.

import { GnbResponse, HeaderRegion } from './types';

const GNB_PATH = '/discovery/api/v1/web/gnb';

export interface FetchGnbParams {
  /** Discovery API base origin. SSR=private(예: https://private-api3.../), CSR=public. 끝 슬래시 유무 무관. */
  baseUrl: string;
  /** 해외/국내. 지정 시 ?isDomestic= 으로 전달(미지정이면 param 생략 → legacy 통합 그룹 폴백). */
  region?: HeaderRegion;
  /** 소비처 주입 fetch 옵션(헤더/credentials/AbortSignal 등). */
  init?: RequestInit;
}

/**
 * baseUrl + region 으로 GNB 요청 URL 을 조립.
 * PST-570 — discovery 백엔드(GET /gnb)는 `?isDomestic=true|false` 만 인식한다.
 *   - domestic → isDomestic=true  (WEB_GNB_DOMESTIC 그룹)
 *   - overseas → isDomestic=false (WEB_GNB_OVERSEAS 그룹)
 *   - region 미지정 → param 생략 (legacy WEB_GNB 통합 그룹 backward-compat)
 * `?region=` 은 백엔드 미선언 파라미터라 무시되고 legacy 그룹으로 폴백하므로 절대 쓰지 않는다.
 */
export function resolveGnbUrl(baseUrl: string, region?: HeaderRegion): string {
  const origin = baseUrl.replace(/\/$/, '');
  const url = `${origin}${GNB_PATH}`;
  if (!region) return url;
  return `${url}?isDomestic=${region === 'domestic'}`;
}

/**
 * GNB 응답을 가져온다. 실패 시 null(헤더는 빈 상태로 폴백).
 * 소비처(앱 SSR / IIFE)가 baseUrl·init 을 주입한다.
 */
export async function fetchGnb({
  baseUrl,
  region,
  init,
}: FetchGnbParams): Promise<GnbResponse | null> {
  try {
    // init.headers 를 통째로 덮어쓰지 않도록 headers 는 병합한다(기본 X-Requested-With 유지 + 소비처 override).
    const { headers: initHeaders, ...restInit } = init ?? {};
    const res = await fetch(resolveGnbUrl(baseUrl, region), {
      credentials: 'include',
      ...restInit,
      headers: { 'X-Requested-With': 'XMLHttpRequest', ...initHeaders },
    });
    if (!res.ok) return null;
    return (await res.json()) as GnbResponse;
  } catch {
    return null;
  }
}
